import React from "react";
import { Link } from "react-router-dom";
import { useAuth, useTier } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import RegionPicker from "@/components/site/RegionPicker";

const PagePlaceholder = ({ title, subtitle, phase = "Phase 2B", testid }) => (
    <div className="px-6 lg:px-10 py-10" data-testid={testid}>
        <div className="mx-auto max-w-[900px]">
            <span className="tc-eyebrow text-[#01696f]">Workspace</span>
            <h1 className="text-[26px] font-medium tracking-tight text-[#0e1416] mt-2">
                {title}
            </h1>
            <p className="mt-3 text-[13.5px] text-[#5a6669] max-w-[60ch] leading-[1.6]">
                {subtitle}
            </p>
            <div className="mt-8 border border-[#d8dcdd] bg-[#ffffff] p-7">
                <div className="flex items-center justify-between mb-3">
                    <span className="tc-eyebrow text-[#5a6669]">
                        Module · not yet active
                    </span>
                    <span className="tc-mono text-[10px] text-[#8a9498]">
                        {phase} deliverable
                    </span>
                </div>
                <p className="text-[13.5px] text-[#0e1416] leading-[1.6]">
                    The catalogue and calibration history surfaces are wired
                    against the domain model in Phase 2B/2C. Schema is already
                    committed (mixes, maturity_settings, strength_calibrations).
                </p>
            </div>
        </div>
    </div>
);

export const Materials = () => (
    <PagePlaceholder
        title="Materials library"
        subtitle="Per-account binder, aggregate, and mix catalogue. Shared across projects with provenance and unit metadata."
        testid="materials-page"
    />
);

export const Calibrations = () => (
    <PagePlaceholder
        title="Calibrations"
        subtitle="Per-mix maturity calibration curves — lab points and field validations. Tracked, versioned, and reusable across projects."
        phase="Phase 2C"
        testid="calibrations-page"
    />
);

export const Account = () => {
    const { account } = useAuth();
    const { tier } = useTier();
    const { regionData, detectionSource } = useRegion();
    const sourceLabel = {
        ip: "auto-detected from IP",
        accept_language: "inferred from browser language",
        fallback: "default — not yet detected",
        account: "saved on your account",
        localStorage: "saved locally on this device",
    }[detectionSource] || "saved on your account";

    return (
        <div className="px-6 lg:px-10 py-10" data-testid="account-page">
            <div className="mx-auto max-w-[720px]">
                <span className="tc-eyebrow text-[#01696f]">Account</span>
                <h1 className="text-[26px] font-medium tracking-tight text-[#0e1416] mt-2">
                    {account?.name}
                </h1>
                <p className="tc-mono text-[12px] text-[#5a6669] mt-1">
                    {account?.email}
                </p>

                <div className="mt-8 border border-[#d8dcdd] bg-[#ffffff]">
                    <Row label="Company" value={account?.company || "—"} />
                    <Row
                        label="Tier"
                        value={
                            <span
                                data-testid="account-tier-value"
                                className="tc-mono text-[12px] text-[#01696f] uppercase tracking-wider"
                            >
                                {tier}
                            </span>
                        }
                    />
                    <Row
                        label="Status"
                        value={account?.subscription?.status || "active"}
                    />
                    <Row
                        label="Region"
                        value={
                            <div
                                data-testid="account-region-field"
                                className="flex flex-col items-end gap-1 min-w-[14rem]"
                            >
                                <RegionPicker placement="field" />
                                <span className="tc-mono text-[10px] text-[#8a9498]">
                                    {regionData?.label} · {sourceLabel}
                                </span>
                            </div>
                        }
                    />
                    <Row
                        label="Member since"
                        value={
                            account?.created_at
                                ? new Date(account.created_at).toLocaleDateString()
                                : "—"
                        }
                        last
                    />
                </div>

                <p className="mt-3 tc-mono text-[11px] text-[#5a6669] leading-[1.5]">
                    Region affects pricing currency and standards references. The engineering core remains universal.
                </p>

                <div className="mt-6 flex items-center justify-between">
                    <span className="tc-mono text-[11px] text-[#5a6669]">
                        Billing — Airwallex Hosted Checkout in a later phase.
                    </span>
                    <Link
                        to="/pricing"
                        data-testid="account-manage-subscription"
                        className="inline-flex items-center gap-2 border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416] px-3.5 py-2 text-[12.5px] transition-colors"
                    >
                        Manage subscription
                    </Link>
                </div>
            </div>
        </div>
    );
};

const Row = ({ label, value, last }) => (
    <div
        className={`flex items-center justify-between px-5 py-3.5 ${
            last ? "" : "border-b border-[#d8dcdd]"
        }`}
    >
        <span className="tc-mono text-[11px] text-[#5a6669] tracking-wider uppercase">
            {label}
        </span>
        <span className="text-[13px] text-[#0e1416]">{value}</span>
    </div>
);
